/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { HTTPError } from '@/components/HTTPError';
import { ActivityRoute } from '@/routes/ActivityRoute';
import { AlertConfigDetailsRoute } from '@/routes/AlertConfigDetailsRoute';
import { APITokenRoute } from '@/routes/APITokenRoute';
import { ArtifactsBrowserRoute } from '@/routes/ArtifactsBrowserRoute';
import { BIDashboardRoute } from '@/routes/BIDashboardRoute';
import { BuildConfigRevisionRoute } from '@/routes/BuildConfigRevisionRoute';
import { CallbackRoute } from '@/routes/CallbackRoute';
import { ChangeAuditRoute } from '@/routes/ChangeAuditRoute';
import { ClearTelemetryRoute } from '@/routes/ClearTelemetryRoute';
import { ConfigRevisionRoute } from '@/routes/ConfigRevisionRoute';
import { ConfigsRoute } from '@/routes/ConfigsRoute';
import { ConfigureNodesJobWizardRoute } from '@/routes/ConfigureNodesJobWizardRoute';
import { CreateAPITokenRoute } from '@/routes/CreateAPITokenRoute';
import { CreateM2MClientRoute } from '@/routes/CreateM2MClientRoute';
import { CreateOAuthClientRoute } from '@/routes/CreateOAuthClientRoute';
import { ApiReferenceRoute } from '@/routes/Docs/ApiReferenceRoute';
import { DownloadReleaseRoute } from '@/routes/DownloadReleaseRoute';
import { FleetManagementRoute } from '@/routes/FleetManagementRoute';
import { GrantOAuthRoute } from '@/routes/GrantOAuthRoute';
import { InstallerTokenRoute } from '@/routes/InstallerTokenRoute';
import { InviteUserRoute } from '@/routes/InviteUserRoute';
import { JobDetailsRoute } from '@/routes/JobDetailsRoute';
import { JobsRoute } from '@/routes/JobsRoute';
import { LoginRoute } from '@/routes/LoginRoute';
import { M2MClientDetailsRoute } from '@/routes/M2MClientDetailsRoute';
import { M2MClientsRoute } from '@/routes/M2MClientsRoute';
import { NodeConfigDetailsRoute } from '@/routes/NodeConfigDetailsRoute';
import { NodeDetailsRoute } from '@/routes/NodeDetailsRoute';
import { NodeE2ERoute } from '@/routes/NodeE2ERoute';
import { NodeGroupDetailsRoute } from '@/routes/NodeGroupDetailsRoute';
import { NodesRoute } from '@/routes/NodesRoute';
import { NotificationManagementRoute } from '@/routes/NotificationManagementRoute';
import { OAuthClientDetailsRoute } from '@/routes/OAuthClientDetailsRoute';
import { OAuthClientRoute } from '@/routes/OAuthClientRoute';
import { PluginsRoute } from '@/routes/PluginsRoute';
import { ReleaseManagementRoute } from '@/routes/ReleaseManagementRoute';
import { ReportsRoute } from '@/routes/ReportsRoute';
import { RevokedInstallerTokenRoute } from '@/routes/RevokedInstallerTokenRoute';
import { SubtenantDetailsRoute } from '@/routes/SubtenantDetailsRoute';
import { SubtenantsRoute } from '@/routes/SubtenantsRoute';
import { SystemInfoRoute } from '@/routes/SystemInfoRoute';
import { TagsRoute } from '@/routes/TagsRoute';
import { TelemetryBetaRoute } from '@/routes/TelemetryBetaRoute';
import { UpdateUserRoute } from '@/routes/UpdateUserRoute';
import { UpdateUserSubtenantsRoute } from '@/routes/UpdateUsersSubtenantsRoute';
import { UserActivityRoute } from '@/routes/UserActivityRoute';
import { UserDetailsRoute } from '@/routes/UserDetailsRoute';
import { UsersRoute } from '@/routes/UsersRoute';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { arePluginsEnabled } from '@/utils/environments';

import { CenterLayout } from './CenterLayout';
import { GuardedRoute } from './GuardedRoute';
import { MainLayout } from './MainLayout';

import './theme/tokens.css';

function Error500() {
  return <HTTPError httpStatus="500" />;
}

export function AppRoutes() {
  const selectedTenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const router = createBrowserRouter(
    [
      {
        path: '/callback',
        errorElement: <Error500 />,
        element: <CallbackRoute />,
      },
      {
        path: '/cloud-artifacts',
        element: <MainLayout />,
        errorElement: <Error500 />,
        children: [
          {
            index: true,
            element: <ArtifactsBrowserRoute />,
          },
        ],
      },
      {
        path: '/node-e2e',
        element: <MainLayout />,
        errorElement: <Error500 />,
        children: [
          {
            index: true,
            element: (
              <GuardedRoute requiredRole="superadmin">
                <NodeE2ERoute />
              </GuardedRoute>
            ),
          },
        ],
      },
      {
        path: '/',
        element: <MainLayout />,
        errorElement: <Error500 />,
        children: [
          {
            index: true,
            element: <CallbackRoute replace />,
          },
          {
            path: '/tenants/:tenantID/',
            element: <Navigate to={`/tenants/${selectedTenantID}/activity`} />,
          },
          {
            path: '/tenants/:tenantID/activity',
            element: <ActivityRoute />,
          },
          {
            path: '/tenants/:tenantID/telemetry',
            element: <Navigate to={`/tenants/${selectedTenantID}/telemetry-viewer`} replace />,
          },
          {
            path: '/tenants/:tenantID/telemetry-viewer',
            element: <TelemetryBetaRoute />,
          },
          {
            path: '/tenants/:tenantID/telemetry-viewer/admin',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <TelemetryBetaRoute adminMode />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/drift-reports',
            element: <ReportsRoute />,
          },
          {
            path: '/tenants/:tenantID/change-audit',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <ChangeAuditRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/bi-dashboard',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <BIDashboardRoute />
              </GuardedRoute>
            ),
          },
          ...(arePluginsEnabled()
            ? [
                {
                  path: '/tenants/:tenantID/node-plugins',
                  element: (
                    <GuardedRoute requiredRole="superadmin">
                      <PluginsRoute />
                    </GuardedRoute>
                  ),
                },
                {
                  path: '/tenants/:tenantID/node-plugins/:pluginId',
                  element: (
                    <GuardedRoute requiredRole="superadmin">
                      <PluginsRoute />
                    </GuardedRoute>
                  ),
                },
              ]
            : []),
          {
            path: '/tenants/:tenantID/api-tokens',
            element: (
              <GuardedRoute requiredRole="admin">
                <APITokenRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/api-tokens/new',
            element: (
              <GuardedRoute requiredRole="admin">
                <CreateAPITokenRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/oauth-clients',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <OAuthClientRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/oauth-clients/new',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <CreateOAuthClientRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/oauth-clients/:oauthClientId',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <OAuthClientDetailsRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/m2m-clients',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <M2MClientsRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/m2m-clients/new',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <CreateM2MClientRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/m2m-clients/:clientId',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <M2MClientDetailsRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/release-management',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <ReleaseManagementRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/job-configure-nodes',
            element: (
              <GuardedRoute requiredRole="admin">
                <ConfigureNodesJobWizardRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/subtenants',
            element: <SubtenantsRoute />,
          },
          {
            path: '/tenants/:tenantID/subtenants/:id',
            element: <SubtenantDetailsRoute />,
          },
          {
            path: '/tenants/:tenantID/jobs/',
            element: <JobsRoute />,
          },
          {
            path: '/tenants/:tenantID/jobs/:jobId',
            element: <JobDetailsRoute />,
          },
          {
            path: '/tenants/:tenantID/nodes',
            element: <NodesRoute />,
          },
          {
            path: '/tenants/:tenantID/fleet-management',
            element: (
              <GuardedRoute requiredRole="admin">
                <FleetManagementRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/fleet-management/:nodeGroupId',
            element: (
              <GuardedRoute requiredRole="admin">
                <NodeGroupDetailsRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/nodes/:id',
            element: <NodeDetailsRoute />,
            children: [
              {
                path: 'clear-telemetry',
                element: (
                  <GuardedRoute requiredRole="superadmin">
                    <ClearTelemetryRoute />
                  </GuardedRoute>
                ),
              },
            ],
          },
          {
            path: '/tenants/:tenantID/node-configs',
            element: (
              <GuardedRoute requiredRole="admin">
                <ConfigsRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/node-configs/:nodeConfigId',
            element: (
              <GuardedRoute requiredRole="admin">
                <NodeConfigDetailsRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/node-configs/:nodeConfigId/revisions/:configRevisionNumber',
            element: (
              <GuardedRoute requiredRole="admin">
                <ConfigRevisionRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/node-configs/:nodeConfigId/revisions/:configRevisionNumber/build',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <BuildConfigRevisionRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/notifications/management',
            element: (
              <GuardedRoute requiredRole="admin">
                <NotificationManagementRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/notifications/management/:alertId',
            element: (
              <GuardedRoute requiredRole="admin">
                <AlertConfigDetailsRoute />
              </GuardedRoute>
            ),
          },
          {
            element: <UsersRoute />,
            path: '/tenants/:tenantID/users',
            children: [
              {
                path: 'invite',
                element: (
                  <GuardedRoute requiredRole="admin">
                    <InviteUserRoute />
                  </GuardedRoute>
                ),
              },
              {
                path: 'update/:userID',
                element: (
                  <GuardedRoute requiredRole="admin">
                    <UpdateUserRoute />
                  </GuardedRoute>
                ),
              },
              {
                path: ':userID/subtenants',
                element: (
                  <GuardedRoute requiredRole="admin">
                    <UpdateUserSubtenantsRoute />
                  </GuardedRoute>
                ),
              },
            ],
          },
          {
            path: '/tenants/:tenantID/users/:userID',
            element: <UserDetailsRoute />,
          },
          {
            path: '/tenants/:tenantID/user-activity',
            element: (
              <GuardedRoute requiredRole="superadmin">
                <UserActivityRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/tenants/:tenantID/tags',
            element: <TagsRoute />,
          },
          {
            path: '/',
            element: (
              <GuardedRoute requiredRole="admin">
                <Outlet />
              </GuardedRoute>
            ),
            children: [
              {
                path: '/tenants/:tenantID/node-releases/:osName',
                element: <DownloadReleaseRoute />,
              },
              {
                path: '/tenants/:tenantID/installer-tokens',
                element: (
                  <GuardedRoute requiredRole="admin">
                    <InstallerTokenRoute />
                  </GuardedRoute>
                ),
              },
              {
                path: '/tenants/:tenantID/installer-tokens/revoked',
                element: (
                  <GuardedRoute requiredRole="admin">
                    <RevokedInstallerTokenRoute />
                  </GuardedRoute>
                ),
              },
            ],
          },
          {
            path: '/tenants/:tenantID/docs/api-reference',
            element: (
              <GuardedRoute requiredRole="admin">
                <ApiReferenceRoute />
              </GuardedRoute>
            ),
          },
          {
            path: '/system-info',
            element: <SystemInfoRoute />,
          },
          {
            path: '*',
            element: <HTTPError httpStatus="404" />,
          },
        ],
      },
      {
        path: '/',
        element: <CenterLayout />,
        children: [
          {
            path: '/login',
            element: <LoginRoute />,
          },
          {
            path: '/grant-oauth',
            element: <GrantOAuthRoute />,
          },
        ],
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true,
      },
    },
  );
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}
